import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount } from "../../lib/auth.js";
import { runAz } from "../../lib/az.js";
import { createFoundryProject, deriveAccountName } from "../../lib/foundry-create.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

export class FoundryCreateCommand extends M8tCommand {
  static paths = [["foundry", "create"]];
  static usage = Command.Usage({
    description: "Create an AI Foundry (AIServices) account + project + model deployment from scratch.",
    details:
      "Non-interactive and idempotent. Creates the AIServices account (custom subdomain + project management), a project, and a model deployment (default gpt-4.1-mini @ capacity 50). Region must be hosted-agent-eligible. Emits the project endpoint as structured output. Re-run is a clean no-op (account/project skipped if present; deployment capacity converges UP, never down).",
    examples: [
      ["Create from zero in a resource group", "$0 foundry create --resource-group rg-m8t-stack --location eastus2"],
      ["Pick names + model", "$0 foundry create --resource-group rg-m8t-stack --location eastus2 --account myfoundry --project m8t --model gpt-4.1-mini"],
      ["Higher capacity for a reasoning model", "$0 foundry create --resource-group rg-m8t-stack --location eastus2 --model gpt-5-mini --capacity 250"],
    ],
  });

  resourceGroup = Option.String("--resource-group");
  location = Option.String("--location");
  account = Option.String("--account");
  project = Option.String("--project", "m8t");
  model = Option.String("--model", "gpt-4.1-mini");
  modelVersion = Option.String("--model-version", "2025-04-14");
  capacity = Option.String("--capacity", "50");
  subscription = Option.String("--subscription");
  skipQuotaCheck = Option.Boolean("--skip-quota-check", false);
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );

    // `typeof ... === "string"`: clipanion's Option descriptor is NOT a string
    // until argv is parsed. Direct property access under test construction returns
    // the raw descriptor object, so we must guard every optional/defaulted option.
    const resourceGroup = typeof this.resourceGroup === "string" ? this.resourceGroup : undefined;
    const location = typeof this.location === "string" ? this.location : undefined;
    if (!resourceGroup) {
      throw new LocalCliError({ code: "USAGE", message: "--resource-group is required.", hint: "Example: m8t foundry create --resource-group rg-m8t-stack --location eastus2" });
    }
    if (!location) {
      throw new LocalCliError({ code: "USAGE", message: "--location is required.", hint: "Example: m8t foundry create --resource-group rg-m8t-stack --location eastus2" });
    }
    const subscriptionOpt = typeof this.subscription === "string" ? this.subscription : undefined;
    if (subscriptionOpt) await runAz(["account", "set", "--subscription", subscriptionOpt]);
    const account = await getAzAccount();
    const subscriptionId = subscriptionOpt ?? account.subscriptionId;
    const accountName = typeof this.account === "string" ? this.account : deriveAccountName(subscriptionId);
    const project = typeof this.project === "string" ? this.project : "m8t";
    const model = typeof this.model === "string" ? this.model : "gpt-4.1-mini";
    const modelVersion = typeof this.modelVersion === "string" ? this.modelVersion : "2025-04-14";
    const capacity = typeof this.capacity === "string" ? Number(this.capacity) : 50;

    const onProgress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const result = await createFoundryProject({
      resourceGroup,
      location,
      account: accountName,
      project,
      model,
      modelVersion,
      capacity,
      // `=== true`: clipanion's Option descriptor is truthy until argv parse, so a
      // bare check would force skipQuotaCheck on under test construction.
      skipQuotaCheck: this.skipQuotaCheck === true,
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          endpoint: result.endpoint,
          accountName: result.accountName,
          accountResourceId: result.accountScope,
          projectName: result.projectName,
          region: result.region,
          model: result.model,
          capacity: result.capacity,
          created: result.created,
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "endpoint", value: result.endpoint },
        { key: "account", value: result.accountName },
        { key: "project", value: result.projectName },
        { key: "region", value: result.region },
        { key: "model", value: `${result.model} (capacity ${String(result.capacity)})` },
      ]) + "\n",
    );
    const made = Object.entries(result.created).filter(([, v]) => v).map(([k]) => k);
    this.context.stdout.write(colors.dim(`created: ${made.length ? made.join(", ") : "nothing (all present)"}\n`));
    this.context.stdout.write(colors.dim(`next: 'm8t deploy --foundry-endpoint ${result.endpoint}' to deploy the gateway.\n`));
    return 0;
  }
}
